import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface FormData {
  name: string;
  email: string;
  phone: string;
  category: string;
  genre: string;
  bio: string;
  website: string;
  video: string;
}

export default function RegisterForm() {
  const { t } = useTranslation('register');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    category: '',
    genre: '',
    bio: '',
    website: '',
    video: '',
  });

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('requiredField');
    if (!formData.email.trim()) {
      newErrors.email = t('requiredField');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }
    if (!formData.phone.trim()) newErrors.phone = t('requiredField');
    if (!formData.category) newErrors.category = t('requiredField');
    if (!formData.genre) newErrors.genre = t('requiredField');
    if (!formData.bio.trim()) newErrors.bio = t('requiredField');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const data = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) data.append(key, value);
      });
      const response = await fetch('https://readdy.ai/api/form/d8eci5uuf4jvh7ti3f20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Error sending. Please try again.');
      }
    } catch {
      alert('Error sending. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="max-w-lg mx-auto text-center bg-amber-50 rounded-2xl p-8 md:p-12">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-amber-600 rounded-full">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('successTitle')}</h2>
            <p className="text-base text-gray-600 mb-8">{t('successMessage')}</p>
            <a href="/" className="text-sm font-medium px-8 py-3.5 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors inline-block">
              {t('backToHome')}
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block text-xs md:text-sm font-semibold text-amber-600 uppercase tracking-widest mb-4">
              {t('formLabel')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('formTitle')}
            </h2>
            <p className="text-base text-gray-600">
              {t('formDescription')}
            </p>
          </div>

          <form id="artist-registration-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('fieldName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder={t('fieldNamePlaceholder')}
                className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent`}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldEmail')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder={t('fieldEmailPlaceholder')}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent`}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldPhone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  placeholder={t('fieldPhonePlaceholder')}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent`}
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldCategory')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.category ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent`}
                >
                  <option value="">Select...</option>
                  <option value="soloist">{t('categorySoloist')}</option>
                  <option value="duo">{t('categoryDuo')}</option>
                  <option value="trio">{t('categoryTrio')}</option>
                  <option value="quartet">{t('categoryQuartet')}</option>
                  <option value="full-band">{t('categoryFullBand')}</option>
                  <option value="dj">{t('categoryDJ')}</option>
                  <option value="other">{t('categoryOther')}</option>
                </select>
                {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldGenre')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={e => handleChange('genre', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.genre ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent`}
                >
                  <option value="">Select...</option>
                  <option value="jazz">{t('genreJazz')}</option>
                  <option value="pop">{t('genrePop')}</option>
                  <option value="rock">{t('genreRock')}</option>
                  <option value="classical">{t('genreClassical')}</option>
                  <option value="latin">{t('genreLatin')}</option>
                  <option value="country">{t('genreCountry')}</option>
                  <option value="electronic">{t('genreElectronic')}</option>
                  <option value="rnb">{t('genreRnb')}</option>
                  <option value="other">{t('genreOther')}</option>
                </select>
                {errors.genre && <p className="text-xs text-red-500 mt-1">{errors.genre}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('fieldBio')} <span className="text-red-500">*</span>
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={e => handleChange('bio', e.target.value)}
                placeholder={t('fieldBioPlaceholder')}
                rows={5}
                maxLength={500}
                className={`w-full px-4 py-3 rounded-lg border ${errors.bio ? 'border-red-500' : 'border-gray-200'} bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent resize-none`}
              />
              <div className="flex justify-between mt-1">
                {errors.bio ? <p className="text-xs text-red-500">{errors.bio}</p> : <span></span>}
                <p className="text-xs text-gray-400">{formData.bio.length}/500</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldWebsite')}
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={e => handleChange('website', e.target.value)}
                  placeholder={t('fieldWebsitePlaceholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fieldVideo')}
                </label>
                <input
                  type="url"
                  name="video"
                  value={formData.video}
                  onChange={e => handleChange('video', e.target.value)}
                  placeholder={t('fieldVideoPlaceholder')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto text-sm font-medium px-8 py-3.5 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? t('submitting') : t('submitButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}